import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import {
  editableResidenceInsuranceType,
  policyType,
  residenceInsuranceTypes,
  standaloneRentersStates,
  rentersPolicyAvailable,
  condoStates,
  isResidentialOwnerStates
} from '@ourbranch/lookups';

import { useToast } from 'core/components/toast';
import { ExpandableCard } from 'core/components/expandable-card';
import { tooltipHoverTexts } from 'core/helpers/constants';
import { track } from 'core/helpers/analytics';
import { trackProspectFunnelEvent } from 'core/helpers/prospect-funnel';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { isPolicyTypeEnabled } from 'common/helpers/environment-helpers';
import { PolicyTypeOption } from './policy-type-option';
import { SwitchPolicy } from './switch-policy';

const policyTypeOptionsByState = (state) => {
  let policies = [
    policyType.Home,
    policyType.HomeBundle,
    policyType.HABundle,
    policyType.Auto,
    policyType.AutoBundle,
    policyType.Farm,
    policyType.FABundle
  ];
  if (rentersPolicyAvailable[state]) {
    policies.push(policyType.ARBundle);
  }

  if (standaloneRentersStates[state]) {
    policies.push(policyType.Renters);
  }

  if (condoStates[state]) {
    policies = [policyType.Condo, policyType.CABundle, ...policies];
  }

  return policies;
};

const monolinePolicyTypes = [
  policyType.Home,
  policyType.Auto,
  policyType.Renters,
  policyType.Condo,
  policyType.FarmHome,
  policyType.FarmRental
];

const policyTypeLabelMap = {
  [policyType.Home]: 'Home',
  [policyType.HomeBundle]: 'Home with bundled discount',
  [policyType.HABundle]: 'Home & auto',
  [policyType.Auto]: 'Auto',
  [policyType.AutoBundle]: 'Auto with bundled discount',
  [policyType.ARBundle]: 'Auto & renters',
  [policyType.Renters]: 'Renters',
  [policyType.Condo]: 'Condo',
  [policyType.CABundle]: 'Condo & Auto',
  CB: 'Condo with Bundled Discount',
  [policyType.Farm]: 'Farm',
  [policyType.FABundle]: 'Farm & Auto',
  [policyType.FarmHome]: 'Home',
  [policyType.FarmRental]: 'Rental'
};

const getPolicyLabel = ({ hasMultilineDiscount, policyType }) => {
  if (monolinePolicyTypes.includes(policyType) && hasMultilineDiscount) {
    return `${policyTypeLabelMap[policyType]} with Multiline Discount`;
  }
  return policyTypeLabelMap[policyType];
};

const inputOptionsMap = {
  [policyType.Condo]: [{ value: policyType.Condo, label: 'Condo' }],
  [policyType.CABundle]: [{ value: policyType.CABundle, label: 'Condo & Auto' }],
  [policyType.Home]: [{ value: policyType.Home, label: 'Home' }],
  [policyType.HABundle]: [{ value: policyType.HABundle, label: 'Home & Auto' }],
  [policyType.HomeBundle]: [{ value: policyType.HomeBundle, label: 'Home with Bundled Discount' }],
  [policyType.Auto]: [{ value: policyType.Auto, label: 'Auto' }],
  [policyType.AutoBundle]: [{ value: policyType.AutoBundle, label: 'Auto with Bundled Discount' }],
  [policyType.ARBundle]: [{ value: policyType.ARBundle, label: 'Auto & Renters' }],
  [policyType.Renters]: [{ value: policyType.Renters, label: 'Renters' }],
  [policyType.Farm]: [{ value: policyType.Farm, label: 'Farm' }],
  [policyType.FABundle]: [{ value: policyType.FABundle, label: 'Farm & Auto' }],
  [policyType.FarmHome]: [{ value: policyType.FarmHome, label: 'Home' }],
  [policyType.FarmRental]: [{ value: policyType.FarmRental, label: 'Rental' }]
};

const getOption = (type, offer) => {
  const isAutoBundle = type === policyType.AutoBundle;
  const isHomeBundle = type === policyType.HomeBundle;
  const option = offer.options.find((opt) => {
    if (isAutoBundle) {
      return [policyType.HABundle, policyType.CABundle].includes(opt.type);
    }
    if (isHomeBundle) {
      return opt.type === policyType.HABundle;
    }
    return opt.type === type;
  });

  if (!option) {
    return null;
  }

  const autoData = {
    autoPrice: option.autoBill,
    autoFrequency: offer.quote.global.autoPaymentType === 'I' ? 'Avg. Monthly' : '6 months'
  };

  const homeData = {
    homePrice: option.homeBill,
    homeFrequency: offer.quote.global.homeownersPaymentType === 'I' ? 'Avg. Monthly' : '12 months'
  };

  const rentersData = {
    rentersPrice: option.rentersBill,
    rentersFrequency: offer.quote.global.rentersPaymentType === 'I' ? 'Avg. Monthly' : '12 months'
  };

  const condoData = {
    condoPrice: option.condoBill,
    condoFrequency: offer.quote.global.condoPaymentType === 'I' ? 'Avg. Monthly' : '12 months'
  };

  const farmData = {
    farmPrice: option.farmBill,
    farmFrequency: offer.quote.global.farmPaymentType === 'I' ? 'Avg. Monthly' : '12 months'
  };

  if (isAutoBundle) {
    return autoData;
  }
  if (isHomeBundle) {
    return homeData;
  }
  return { ...homeData, ...autoData, ...rentersData, ...condoData, ...farmData };
};

const getResidenceInsuranceTypeByPolicyTypeAndState = (type, state) => {
  if (editableResidenceInsuranceType.includes(state)) {
    switch (type) {
      case policyType.HABundle:
        return residenceInsuranceTypes.Home;
      case policyType.ARBundle:
      case policyType.Renters:
        return residenceInsuranceTypes.Renters;
      case policyType.CABundle:
        return residenceInsuranceTypes.Condo;
      default:
        return residenceInsuranceTypes.None_NotApplicable;
    }
  }

  if (isResidentialOwnerStates.includes(state)) {
    return residenceInsuranceTypes.None_NotApplicable;
  }

  return null;
};
const PolicyPickerCard = observer(() => {
  const {
    offer: store,
    offer: {
      selectedOption,
      offer: {
        quote: {
          existingCustomer,
          forcedMultiPolicyDiscount,
          offerings: { monolineAutoRejectCode },
          selectedOption: quoteSelectedOption
        },
        rejections,
        options,
        parentId
      },
      priorQuoteWithPreBindUWRejections: { ineligibleForHome, ineligibleForAuto }
    },
    account: { sendEventAsMember }
  } = useStore();

  const toast = useToast();
  const session = useSession();
  const { isAgency } = session;
  const { values, setFieldValue } = useFormikContext();
  const [landedOnUnmergedOfferAndFiredEvent, setLandedOnOfferAndFiredEvent] = useState(false);
  const [clickedBundleOptionAndFiredEvent, setClickedBundleOptionAndFiredEvent] = useState(false);

  const forcedMultiPolicyDiscountValue = forcedMultiPolicyDiscount || values?.forcedMultiPolicyDiscount;
  const hasMultilineDiscount = forcedMultiPolicyDiscountValue && forcedMultiPolicyDiscountValue !== 'N';
  const linkedToExistingCustomer = !!existingCustomer?.id;

  const hasMonolineAutoOption = options.some((opt) => {
    return opt.type === policyType.Auto;
  });

  const isIneligibleForAuto =
    !hasMonolineAutoOption || monolineAutoRejectCode || ineligibleForAuto || rejections?.some((r) => r.type === 'auto');
  const isIneligibleForHome = ineligibleForHome || rejections?.some((r) => r.type === 'home');

  const policyTypeOptions = useMemo(() => {
    const baseOptions = policyTypeOptionsByState(store?.state);

    // Filter by environment-configured policy types
    const envFilteredOptions = baseOptions.filter(isPolicyTypeEnabled);

    const filteredOptions = [];
    envFilteredOptions.forEach((o) => {
      const isAutoBundle = o === policyType.AutoBundle;
      const isHomeBundle = o === policyType.HomeBundle;
      let shouldAddOption = false;

      if (isAutoBundle && !isIneligibleForAuto && linkedToExistingCustomer) {
        shouldAddOption = !!options.find((opt) =>
          [policyType.Auto, policyType.CABundle, policyType.HABundle].includes(opt.type)
        );
      } else if (isHomeBundle && !isIneligibleForHome && linkedToExistingCustomer) {
        shouldAddOption = !!options.find((opt) => [policyType.Home, policyType.HABundle].includes(opt.type));
      } else {
        shouldAddOption = !!options.find((opt) => opt.type === o);
      }
      if (shouldAddOption) {
        filteredOptions.push(o);
      }
    });

    return filteredOptions.sort((a, b) => {
      if (a === policyType.HABundle) {
        return -1;
      }
      if (b === policyType.HABundle) {
        return 1;
      }
      if (a === policyType.HomeBundle) {
        return -1;
      }
      if (b === policyType.HomeBundle) {
        return 1;
      }
      if (a === policyType.AutoBundle) {
        return -1;
      }
      if (b === policyType.AutoBundle) {
        return 1;
      }
      if (a === policyType.ARBundle) {
        return -1;
      }
      if (b === policyType.ARBundle) {
        return 1;
      }
      if (a === policyType.CABundle) {
        return -1;
      }
      if (b === policyType.CABundle) {
        return 1;
      }
      return 0;
    });
  }, [store.state, options, linkedToExistingCustomer, isIneligibleForAuto, isIneligibleForHome]);

  const showAppcuesEvents =
    store.offer?.quote?.global?.fromRC1 &&
    !parentId &&
    ['H', 'A'].includes(selectedOption) &&
    options.some((option) => option.type === 'HA') &&
    !(landedOnUnmergedOfferAndFiredEvent && clickedBundleOptionAndFiredEvent) &&
    !store.showRaterInterstitialModal;

  useEffect(() => {
    if (showAppcuesEvents && !landedOnUnmergedOfferAndFiredEvent) {
      setLandedOnOfferAndFiredEvent(true);
      track('Upsell bundle offer', {
        isAgency,
        policyType: quoteSelectedOption
      });
    }
  }, [showAppcuesEvents, landedOnUnmergedOfferAndFiredEvent, isAgency, quoteSelectedOption]);

  const onClick = useCallback(
    (type, disabled) => {
      if (!disabled) {
        if (['HA', 'CA', 'RA'].includes(type) && showAppcuesEvents) {
          setClickedBundleOptionAndFiredEvent(true);
          track('On upsold bundle offer', {
            isAgency,
            policyType: quoteSelectedOption
          });
        }
        if (type !== store.selectedOption) {
          trackProspectFunnelEvent({
            sendEventAsMember,
            eventName: 'Staff Funnel: Change Policy Type',
            offer: store?.offer,
            session,
            extraProps: {
              selectedOption: type,
              policyType: store.selectedOption
            }
          });
          track('Staff User: Change Policy Type', {
            offerId: store?.offer?.id,
            webUserId: store?.offer?.webUserId,
            agent: session?.user?.username,
            isAgency,
            newPolicyType: type,
            previousPolicyType: store.selectedOption
          });
        }
        // store.selectedOption drives live UI like tab visibility — keep it
        // in sync with the picker. The form's dirty flag stays correct because
        // initialValues.selectedOption is baselined off the persisted
        // offer.quote.selectedOption (in provider.jsx), not this store field. (#30986)
        store.setSelectedOption(type);
        store.setNeedsPreBindUWForm();
        setFieldValue('noBindHome', type === policyType.AutoBundle);
        setFieldValue('noBindAuto', type === policyType.HomeBundle);

        // Set residenceInsuranceType when policy type changes
        const residenceInsuranceType = getResidenceInsuranceTypeByPolicyTypeAndState(type, store.state);
        setFieldValue('auto.residenceInsuranceType', residenceInsuranceType);

        // remove umbrella if no longer on a bundle that supports it
        const isUmbrellaEligibleBundle = [
          policyType.HABundle,
          policyType.HomeBundle,
          policyType.CABundle,
          policyType.AutoBundle
        ].includes(type);
        if (!isUmbrellaEligibleBundle && values.includeUmbrella) {
          setFieldValue('includeUmbrella', false);
          toast.notify({
            type: 'success',
            message: 'Umbrella coverage has been removed from this offer since it is no longer a bundled offer.'
          });
        }
      }
    },
    [setFieldValue, store, showAppcuesEvents, values, quoteSelectedOption, isAgency, toast, sendEventAsMember, session]
  );

  useEffect(() => {
    store.setNotifications();
  }, [selectedOption, store]);

  return (
    <div id="offer_policy_types">
      <ExpandableCard title="Type" badge={policyTypeLabelMap[values.selectedOption]} collapsible={false}>
        {policyTypeOptions.map((type, ix) => {
          const option = getOption(type, store.offer);

          if (!option) {
            return null;
          }

          let disabled = false;
          let tooltip = null;
          let noHome = false;
          let noAuto = false;

          if (isIneligibleForAuto) {
            noAuto = type === policyType.Auto || (!linkedToExistingCustomer && type === policyType.AutoBundle);
            if (type === policyType.Auto) {
              tooltip = tooltipHoverTexts.noMonolineAuto;
            } else if (!linkedToExistingCustomer && type === policyType.AutoBundle) {
              tooltip = tooltipHoverTexts.noMonolineAutoWBundle;
            }
          }

          noHome = isIneligibleForHome && [policyType.HomeBundle, policyType.Home, policyType.HABundle].includes(type);

          disabled =
            ([policyType.HomeBundle, policyType.AutoBundle].includes(type) && !linkedToExistingCustomer) ||
            noAuto ||
            noHome;

          const label = getPolicyLabel({ hasMultilineDiscount, policyType: type });

          return (
            <PolicyTypeOption
              onClick={onClick}
              key={ix}
              id={
                [policyType.ARBundle, policyType.HABundle, policyType.CABundle].includes(type)
                  ? 'policy-option-selector-bundle'
                  : undefined
              }
              option={option}
              type={type}
              state={store.state}
              label={label}
              disabled={disabled}
              fieldOptions={inputOptionsMap[type]}
              tooltip={tooltip}
              noHome={noHome}
            />
          );
        })}
        <SwitchPolicy />
      </ExpandableCard>
    </div>
  );
});

export default PolicyPickerCard;
